import React, {useState, useEffect} from "react";
import { Fancybox } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";
import {PhotoModel} from "../types/protocols";
import {FirebaseImage, ImageSize, useFirebaseImage} from "../Helpers/ImageLoader";
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase';
import { useUsersData } from '../Helpers/UsersContext';
import { buildPhotoCaption } from '../Helpers/photoCaptionUtils';
import ConfirmationModal from './ConfirmationModal';
import "./ClickableImage.css";

interface ClickableImageProps {
    photo: PhotoModel;
    buildingKey: string;
    altText?: string;
    size?: number;
    /** Fill the parent's width and height instead of a fixed size-by-size box. `size` still picks the image resolution. */
    fill?: boolean;
    disableClick?: boolean;
    thumbnail?: boolean;
    showDate?: boolean;
    onImageClick?: (photo: PhotoModel, fullUrl: string) => void;
    highlighted?: boolean;
    showDeleteButton?: boolean;
    onDeleteClick?: (photo: PhotoModel) => void;
}

const ClickableImage: React.FC<ClickableImageProps> = ({
                                                           photo,
                                                           buildingKey,
                                                           altText = "Component Photo",
                                                           size = 125,
                                                           fill = false,
                                                           disableClick = false,
                                                           thumbnail = true,
                                                           showDate = size > 80 && !!(photo.date && photo.date.length !== 0),
                                                           onImageClick,
                                                           highlighted = false,
                                                           showDeleteButton = false,
                                                           onDeleteClick,
                                                       }) => {

    const imageSize = size < 200 ? ImageSize.THUMBNAIL_SMALL : size < 600 ? ImageSize.THUMBNAIL_MEDIUM : ImageSize.FULL
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);

    const { src, isLoading, hasError } = useFirebaseImage(photo.storagePath);
    const [user] = useAuthState(auth);
    const { users } = useUsersData();

    // Check if current user is admin (only when delete button is shown)
    useEffect(() => {
        if (!showDeleteButton) return;
        if (user && users) {
            const currentUser = users.find(u => u.key === user.uid);
            setIsAdmin(currentUser?.admin || false);
        }
    }, [user, users, showDeleteButton]);

    const handleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        if (disableClick) return;

        // Check if the click was on the delete button
        const target = e.target as Element;
        if (target.closest('.delete-button')) {
            return;
        }

        if (onImageClick) {
            onImageClick(photo, src)
            return;
        }

        const fancyboxOptions: any = {
            animated: true,
            showClass: "fancybox-fadeIn",
        };

        // Add custom buttons if delete functionality is enabled
        if (showDeleteButton && onDeleteClick) {
            fancyboxOptions.onInit = (fancybox: any) => {
                // The viewer's delete button asks the same question as the
                // thumbnail's: close the viewer and open the confirmation.
                const requestDelete = (e: Event) => {
                    e.preventDefault();
                    e.stopPropagation();
                    fancybox.close();
                    setShowDeleteModal(true);
                };
                // Try multiple selectors to find the toolbar
                const toolbarSelectors = [
                    '.fancybox__toolbar',
                    '.fancybox-toolbar',
                    '[data-fancybox-toolbar]',
                    '.fancybox__container .fancybox__toolbar'
                ];
                
                let toolbar = null;
                for (const selector of toolbarSelectors) {
                    toolbar = fancybox.container.querySelector(selector);
                    if (toolbar) {
                        break;
                    }
                }
                
                if (!toolbar) {
                    // Try to find it in the document
                    for (const selector of toolbarSelectors) {
                        toolbar = document.querySelector(selector);
                        if (toolbar) {
                            break;
                        }
                    }
                }
                
                if (toolbar) {
                    const deleteButton = document.createElement('button');
                    deleteButton.className = 'fancybox__button fancybox__button--delete';
                    deleteButton.innerHTML = '🗑️';
                    deleteButton.title = 'Ta bort bild';
                    deleteButton.style.cssText = `
                        background: rgba(255, 255, 255, 0.1);
                        border: none;
                        border-radius: 4px;
                        color: white;
                        cursor: pointer;
                        font-size: 16px;
                        height: 40px;
                        margin: 0 4px;
                        padding: 0 12px;
                        transition: background-color 0.2s;
                        z-index: 9999;
                    `;
                    deleteButton.addEventListener('click', requestDelete);
                    deleteButton.addEventListener('mouseenter', () => {
                        deleteButton.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                    });
                    deleteButton.addEventListener('mouseleave', () => {
                        deleteButton.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                    });
                    
                    // Try to insert before the close button
                    const closeButton = toolbar.querySelector('.fancybox__button--close');
                    if (closeButton) {
                        toolbar.insertBefore(deleteButton, closeButton);
                    } else {
                        toolbar.appendChild(deleteButton);
                    }
                } else {
                    // Alternative approach: add button to the container directly
                    setTimeout(() => {
                        const container = document.querySelector('.fancybox__container');
                        if (container) {
                            const deleteButton = document.createElement('button');
                            deleteButton.className = 'fancybox__button fancybox__button--delete';
                            deleteButton.innerHTML = '🗑️';
                            deleteButton.title = 'Ta bort bild';
                            deleteButton.style.cssText = `
                                position: absolute;
                                top: 20px;
                                right: 60px;
                                background: rgba(255, 255, 255, 0.1);
                                border: none;
                                border-radius: 4px;
                                color: white;
                                cursor: pointer;
                                font-size: 16px;
                                height: 40px;
                                padding: 0 12px;
                                transition: background-color 0.2s;
                                z-index: 9999;
                            `;
                            deleteButton.addEventListener('click', requestDelete);
                            deleteButton.addEventListener('mouseenter', () => {
                                deleteButton.style.backgroundColor = 'rgba(255, 255, 255, 0.2)';
                            });
                            deleteButton.addEventListener('mouseleave', () => {
                                deleteButton.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                            });
                            container.appendChild(deleteButton);
                        }
                    }, 100);
                }
            };
        }

        Fancybox.show(
            [
                {
                    src: src,
                    caption: buildPhotoCaption(photo, users),
                }
            ],
            fancyboxOptions
        );
    };

    const handleDeleteClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = () => {
        if (onDeleteClick) {
            onDeleteClick(photo);
        }
        setShowDeleteModal(false);
    };

    const handleCancelDelete = () => {
        setShowDeleteModal(false);
    };



    return (
        <div
            className={`clickable-image-container ${disableClick ? 'disabled' : ''}`}
            style={{
                position: 'relative',
                width: fill ? '100%' : size,
                height: fill ? '100%' : size,
                borderRadius: '5px',
                overflow: 'hidden',
                // Selection ring matching the "Samtliga bilder" thumbnails
                boxShadow: highlighted ? '0 0 0 2px #fff, 0 0 0 5px #007bff' : undefined,
            }}
        >
            <div
                data-fancybox="gallery"
                data-caption={buildPhotoCaption(photo, users)}
                onClick={handleClick}
                style={{
                    display: 'block',
                    width: '100%',
                    height: '100%',
                }}
            >
                <FirebaseImage
                    key={photo.key}
                    alt={altText}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        ...(disableClick ? {} : { cursor: 'pointer' }),
                    }}
                    size={imageSize}
                    storagePath={photo.storagePath}
                />
            </div>
            {showDate && photo.date && (
                <div
                    style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.7))',
                        color: '#fff',
                        fontSize: '11px',
                        padding: '12px 4px 4px',
                        textAlign: 'center',
                        pointerEvents: 'none',
                    }}
                >
                    {photo.date.split(" ")[0]}
                </div>
            )}

            {/* Delete button - only show for admins */}
            {showDeleteButton && isAdmin && (
                <button
                    className="delete-button"
                    onClick={handleDeleteClick}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                    style={{
                        position: 'absolute',
                        top: '5px',
                        right: '5px',
                        background: 'rgba(0, 0, 0, 0.8)',
                        border: 'none',
                        borderRadius: '50%',
                        width: '18px',
                        height: '18px',
                        color: 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        zIndex: 1000,
                        transition: 'all 0.2s',
                        pointerEvents: 'auto',
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(75, 75, 75, 1)';
                        e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
                    }}
                >
                    <img 
                        src="/Images/UI/Trash.png" 
                        alt="Delete" 
                        style={{ 
                            width: '10px', 
                            height: '10px',
                            display: 'block',
                            filter: 'invert(1)'
                        }} 
                    />
                </button>
            )}

            {showDeleteButton && (
                <ConfirmationModal
                    show={showDeleteModal}
                    title="Ta bort bild"
                    message="Vill du ta bort bilden?"
                    confirmText="Ta bort bild"
                    onConfirm={handleConfirmDelete}
                    onCancel={handleCancelDelete}
                />
            )}
        </div>
    );
};

export default ClickableImage;
